/**
 * "Today" at the shop.
 *
 * One place that answers "what calendar day is it in Miami?", shared by the
 * promotions list (offer expiry) and the /book form (a preferred date must be
 * today or later). Extracted from lib/promotions.ts so a fix to the boundary
 * logic reaches both; before, /book carried its own copy.
 */

/**
 * The timezone "today" is judged in. A calendar day at the shop cannot be read
 * off the visitor's browser clock or off UTC: either would expire an offer, or
 * reject a valid same-day request, for the four to five hours after 8pm local.
 */
export const SHOP_TIME_ZONE = 'America/New_York';

// Cached at module scope: constructing an Intl formatter is the expensive part.
const SHOP_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: SHOP_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Today's calendar date at the shop as `YYYY-MM-DD`.
 *
 * Comparing `YYYY-MM-DD` strings lexicographically is exact for this format
 * and sidesteps date arithmetic entirely. Using `toISOString()` instead would
 * compare against the UTC date, expiring every offer four to five hours early
 * for the Miami audience this site serves — between 8pm and midnight local,
 * a live offer would vanish.
 *
 * Assembled from `formatToParts` rather than `format()`. Reading the formatted
 * string would bake in an assumption about `en-CA` emitting ISO order — true
 * under full ICU, but a small-ICU runtime falls back to `MM/DD/YYYY`, and a
 * `split("-")` on that would yield NaN, an Invalid Date, and a `RangeError`
 * thrown inside a Server Component: a 500 on the home page rather than a
 * missing badge. Parts are labelled, so order cannot matter.
 */
export function shopToday(now: Date = new Date()): string {
  const parts = SHOP_DATE.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}
